<template>
  <div class="row g-4">
    <div class="col-12">
      <div
        class="d-flex flex-wrap justify-content-between align-items-start gap-3"
      >
        <div>
          <h5 class="mb-0">Annual recurring revenue (forward-looking)</h5>
          <small class="text-muted">
            The KPI sheet's method: (spot + spot with min price) GWh × spot
            price, plus contracted forwards at their own price, plus
            subscriptions. GO sales are booked as revenue (Soldera holds the
            principal), so the headline is gross; net ARR keeps each company's
            fee share. PPA and self-managed volume are excluded; spot volume is
            contractual (the producer's standing instruction to sell its monthly
            production).
          </small>
        </div>
        <div class="d-flex align-items-center gap-3 flex-wrap">
          <form
            class="input-group input-group-sm price-input"
            @submit.prevent="applyPrice"
          >
            <span class="input-group-text">GO price</span>
            <input
              v-model="priceInput"
              type="number"
              min="0"
              step="0.01"
              class="form-control"
              :placeholder="derivedPriceLabel"
              aria-label="GO price assumption in EUR per MWh"
            />
            <span class="input-group-text">€/MWh</span>
            <button
              type="submit"
              class="btn btn-outline-primary"
              :disabled="priceInput === ''"
            >
              Use
            </button>
            <button
              v-if="goPrice !== null"
              type="button"
              class="btn btn-outline-secondary"
              @click="resetPrice"
            >
              Derived
            </button>
          </form>
          <MethodologyModal title="ARR methodology (forward-looking)">
            <p>
              For every calendar month <em>M</em> (up to the current month),
              following the KPI sheet's "Actual" tab:
            </p>
            <pre class="bg-light p-2 rounded small mb-3">
ARR spot    = (Spot sale + Spot with min price + Forward planned, not yet contracted) MWh × spot GO price
              Polish and nuclear volume × their own market quotes when available, else the spot GO price
ARR forward = contracted forward MWh × its contract price
GO gross    = ARR spot + ARR forward                       ← GMV of sold volume, booked as revenue
Total ARR   = GO gross + platform fees ARR                 ← the sheet's "ARR total" (headline)
GO net ARR  = Σ over companies  company GMV × company fee
Net ARR     = GO net ARR + platform fees ARR               ← the sheet's "Net ARR"</pre>
            <dl>
              <dt>Volume split</dt>
              <dd>
                The allocation simulation that feeds the admin Allocation page
                splits each company's projected next-12-month production into
                PPA, forward (contracted / still to be sold), self-managed and
                spot (market or min price): the same numbers as the sheet's PPA
                / Forward sale / Self managed / Spot rows. PPA and self-managed
                volume earn no commission and are excluded from ARR. The split
                is a snapshot of today's settings; for past months it is applied
                to the devices onboarded by then, so the series shows how the
                base grew.
              </dd>
              <dt>Why spot counts as contracted</dt>
              <dd>
                A producer on a spot strategy has instructed Soldera, in a
                binding and automatic way, to sell its monthly production. The
                instruction can be changed, which is what the retention tab
                tracks; it is not a forecast of whether the producer will sell.
              </dd>
              <dt>GWh under management</dt>
              <dd>
                The platform's production projection per registered device of
                onboarded, non-test companies (power × technology/country curve,
                or the manual estimate override), counted from the month it was
                added until removed.
              </dd>
              <dt>Spot GO price</dt>
              <dd>
                Polish wind/solar and nuclear volume is priced off its own
                market series (latest quote, production-year vintage preferred),
                the way the KPI sheet carries separate Polish and nuclear
                prices. When a series has no quote, the default spot price is
                applied to that volume and reported as such, so the assumption
                above reaches Polish and nuclear volume only in that case. For
                everything else: by default the volume-weighted price achieved
                on completed, delivered or ready-to-transfer sales in the
                trailing three months, falling back to the market benchmark bid.
                Type an assumption above to replace it for every month, the way
                the sheet uses a hand-entered "Spot current year GO price".
              </dd>
              <dt>Forward price</dt>
              <dd>
                The prices of each company's forward deliveries still to ship,
                weighted by the volume left to deliver (the sheet's "Avg forward
                price").
              </dd>
              <dt>Fee</dt>
              <dd>
                Each company's actual commission rate (special percentage, or
                the standard tier from its owner's total kW). Flat-fee companies
                contribute no commission; their subscription is in platform
                fees. The blended rate is shown for comparison with the sheet's
                "our avg fee".
              </dd>
              <dt>Platform fees ARR</dt>
              <dd>
                Contracted flat fees (the sheet's "ARR SaaS") annualized from
                their invoice period, plus the trailing-12-month run-rate of
                usage-based fees (cancellation, procurement, issuance,
                transfer), which have no contracted volume.
              </dd>
              <dt>Realized (comparison)</dt>
              <dd>
                Settled net revenue over the trailing 12 months. Not part of
                ARR; shown so forecast and actual can be compared.
              </dd>
            </dl>
            <p class="mb-0 text-muted small">
              Backend: <code>app/money/admin/arr_service.py</code>. Click a row
              for the per-company breakdown.
            </p>
          </MethodologyModal>
        </div>
      </div>
    </div>

    <LoadingIndicatorComponent v-if="isLoading" message="Calculating ARR..." />

    <template v-else-if="data && latest">
      <div class="col-12">
        <div class="row g-3">
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 border-primary">
              <div class="card-body">
                <h6 class="card-subtitle mb-2 text-muted">
                  Total ARR · {{ periodLabel(latest.year, latest.month) }}
                </h6>
                <h3 class="card-title text-primary mb-0">
                  {{ formatEurOrDash(latest.total_arr) }}
                </h3>
                <small class="text-muted">{{ headlineSubtitle(latest) }}</small>
                <div v-if="totalChange !== null" class="mt-2 pt-2 border-top">
                  <small
                    :class="totalChange >= 0 ? 'text-success' : 'text-danger'"
                  >
                    <font-awesome-icon
                      :icon="totalChange >= 0 ? 'arrow-up' : 'arrow-down'"
                      class="me-1"
                    />
                    {{ formatRatio(Math.abs(totalChange)) }} vs previous month
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 border-info">
              <div class="card-body">
                <h6 class="card-subtitle mb-2 text-muted">Net ARR</h6>
                <h3 class="card-title text-info mb-0">
                  {{ formatEurOrDash(latest.net_arr_total) }}
                </h3>
                <small class="text-muted">
                  GO net{{ formatEurOrDash(latest.go_net_arr) }} (GMV × blended
                  fee {{ formatRatio(latest.blended_fee_rate) }}) + platform
                  fees
                </small>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 border-success">
              <div class="card-body">
                <h6 class="card-subtitle mb-2 text-muted">Platform fees ARR</h6>
                <h3 class="card-title text-success mb-0">
                  {{ formatMoney(latest.platform_fees_arr) }} €
                </h3>
                <small class="text-muted">
                  {{ formatMoney(latest.flat_fee_contracted_arr) }} € contracted
                  flat fees · {{ formatMoney(latest.usage_fee_run_rate) }} €
                  usage run-rate
                </small>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 border-secondary">
              <div class="card-body">
                <h6 class="card-subtitle mb-2 text-muted">Sold volume</h6>
                <h3 class="card-title text-secondary mb-0">
                  {{ formatWholeNumber(soldGwh(latest)) }} GWh
                </h3>
                <small class="text-muted">
                  of {{ formatWholeNumber(latest.gwh_total) }} GWh under
                  management ({{ latest.device_count }} devices,
                  {{ latest.company_count }} companies) · spot
                  {{ priceLabel(latest) }} €/MWh
                  <span class="badge bg-light text-muted fw-normal">{{
                    ARR_PRICE_SOURCE_LABELS[latest.price_source]
                  }}</span>
                  · forward
                  {{
                    latest.forward_price === null
                      ? "–"
                      : latest.forward_price.toFixed(2)
                  }}
                  €/MWh
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-7">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">ARR by month</h5>
            <p class="text-muted small mb-2">
              Bars: gross ARR (spot, forward, platform fees). Lines: net ARR,
              and gross revenue actually settled in the trailing 12 months.
            </p>
            <div class="chart-lg">
              <canvas ref="arrChartRef"></canvas>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-5">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">GWh by strategy and spot price</h5>
            <p class="text-muted small mb-2">
              The sheet's PPA / Forward / Self managed / Spot rows. Only spot
              and forward volume earn commission.
            </p>
            <div class="chart-lg">
              <canvas ref="gwhChartRef"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">ARR table</h5>
            <p class="text-muted small">
              Click a row for the per-company, per-technology and per-country
              breakdown.
            </p>
            <DataTable
              :value="tableRows"
              dataKey="period"
              sortField="period"
              :sortOrder="-1"
              class="p-datatable-sm border rounded"
              :rowClass="clickableRow"
              @row-click="(event) => $emit('openMonth', event.data.source)"
            >
              <Column field="period" header="Period" sortable />
              <Column field="gwh_total" header="GWh sold / total" sortable>
                <template #body="{ data }">
                  {{ formatWholeNumber(soldGwh(data)) }} /
                  {{ formatWholeNumber(data.gwh_total) }}
                  <div class="small text-muted">
                    spot
                    {{
                      formatWholeNumber(data.gwh_spot + data.gwh_spot_min_price)
                    }}
                    · fwd
                    {{
                      formatWholeNumber(
                        data.gwh_forward_committed + data.gwh_forward_to_be_sold
                      )
                    }}
                    · PPA {{ formatWholeNumber(data.gwh_ppa) }} · self
                    {{ formatWholeNumber(data.gwh_self_managed) }}
                  </div>
                </template>
              </Column>
              <Column field="go_price" header="Spot / fwd price" sortable>
                <template #body="{ data }">
                  {{ priceLabel(data) }} /
                  {{
                    data.forward_price === null
                      ? "–"
                      : data.forward_price.toFixed(2)
                  }}
                  <div class="small text-muted">
                    {{
                      ARR_PRICE_SOURCE_LABELS[
                        data.price_source as ArrPriceSource
                      ]
                    }}
                  </div>
                  <div class="small text-muted">
                    {{ segmentPriceLabel(data) }}
                  </div>
                </template>
              </Column>
              <Column field="blended_fee_rate" header="Fee" sortable>
                <template #body="{ data }">{{
                  formatRatio(data.blended_fee_rate)
                }}</template>
              </Column>
              <Column field="go_gross_arr" header="GO gross ARR (GMV)" sortable>
                <template #body="{ data }"
                  ><span class="text-secondary">{{
                    formatEurOrDash(data.go_gross_arr)
                  }}</span></template
                >
              </Column>
              <Column field="go_net_arr" header="GO net ARR" sortable>
                <template #body="{ data }"
                  ><span class="text-info">{{
                    formatEurOrDash(data.go_net_arr)
                  }}</span></template
                >
              </Column>
              <Column
                field="platform_fees_arr"
                header="Platform fees ARR"
                sortable
              >
                <template #body="{ data }">
                  <span class="text-success"
                    >{{ formatMoney(data.platform_fees_arr) }} €</span
                  >
                  <div class="small text-muted">
                    flat {{ formatMoney(data.flat_fee_contracted_arr) }} · usage
                    {{ formatMoney(data.usage_fee_run_rate) }}
                  </div>
                </template>
              </Column>
              <Column field="total_arr" header="Total ARR (gross)" sortable>
                <template #body="{ data }">
                  <span class="fw-bold text-primary">{{
                    formatEurOrDash(data.total_arr)
                  }}</span>
                  <small
                    v-if="data.change !== null"
                    class="ms-1"
                    :class="data.change >= 0 ? 'text-success' : 'text-danger'"
                  >
                    {{ data.change >= 0 ? "+" : "-"
                    }}{{ formatRatio(Math.abs(data.change)) }}
                  </small>
                </template>
              </Column>
              <Column field="net_arr_total" header="Net ARR" sortable>
                <template #body="{ data }">
                  <span class="fw-semibold text-info">{{
                    formatEurOrDash(data.net_arr_total)
                  }}</span>
                </template>
              </Column>
              <Column
                field="realized_gross_ttm"
                header="Realized TTM gross / net"
                sortable
              >
                <template #body="{ data }">
                  <span v-if="data.realized_gross_ttm !== null">
                    {{ formatMoney(data.realized_gross_ttm) }} € /
                    {{ formatMoney(data.realized_net_ttm ?? 0) }} €
                  </span>
                  <span v-else class="text-muted">–</span>
                  <div v-if="data.one_off_ttm" class="small text-muted">
                    incl. one-offs {{ formatMoney(data.one_off_ttm) }} €
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="col-12 text-muted">No ARR data for this range.</div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatMoney } from "@/composables/formatUtils";
import type { ChartConfiguration } from "chart.js";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import MethodologyModal from "./MethodologyModal.vue";
import {
  ARR_PRICE_SOURCE_LABELS,
  ARR_STRATEGIES,
  changeRatio,
  formatEurOrDash,
  formatRatio,
  formatWholeNumber,
  periodLabel,
} from "./financialDashboardMetrics";
import type {
  ArrPriceSource,
  ArrResponse,
  ArrRow,
} from "./financialDashboardTypes";
import { useChartCanvas } from "./useChartCanvas";

interface ArrTableRow extends ArrRow {
  period: string;
  source: ArrRow;
  change: number | null;
}

const props = defineProps<{
  data: ArrResponse | null;
  isLoading: boolean;
}>();

defineEmits<{
  openMonth: [row: ArrRow];
}>();

/** GO price assumption in EUR/MWh; null lets the backend derive it. The parent refetches when it changes. */
const goPrice = defineModel<number | null>("goPrice", { required: true });

const priceInput = ref(goPrice.value === null ? "" : String(goPrice.value));
const arrChartRef = useTemplateRef<HTMLCanvasElement>("arrChartRef");
const gwhChartRef = useTemplateRef<HTMLCanvasElement>("gwhChartRef");
const arrChart = useChartCanvas(arrChartRef);
const gwhChart = useChartCanvas(gwhChartRef);

// Stable reference: an inline arrow in the template is a new prop every render and makes
// PrimeVue's DataTable re-render itself endlessly.
const clickableRow = () => "cursor-pointer";

const rows = computed(() => props.data?.rows ?? []);
const latest = computed(() => rows.value.at(-1) ?? null);
const totalChange = computed(() =>
  latest.value
    ? changeRatio(latest.value.total_arr, rows.value.at(-2)?.total_arr)
    : null
);
const headlineSubtitle = (row: ArrRow): string =>
  `GO gross (spot ${formatEurOrDash(row.arr_spot)} + forward ${formatEurOrDash(row.arr_forward)}) + platform fees · net ${formatEurOrDash(row.net_arr_total)}`;
const derivedPriceLabel = computed(() =>
  latest.value?.go_price != null
    ? `derived: ${latest.value.go_price.toFixed(2)}`
    : "derived"
);

const tableRows = computed<ArrTableRow[]>(() =>
  rows.value.map((row, index) => ({
    ...row,
    period: periodLabel(row.year, row.month),
    source: row,
    change: changeRatio(row.total_arr, rows.value[index - 1]?.total_arr),
  }))
);

/** Polish and nuclear quotes applied to that volume, e.g. "PL 0.72 · nuclear 0.07". */
const segmentPriceLabel = (row: ArrRow): string =>
  Object.entries(row.segment_prices)
    .filter(([key, value]) => key !== "default" && value.price !== null)
    .map(([key, value]) => `${key} ${value.price?.toFixed(2)}`)
    .join(" · ");
const priceLabel = (row: ArrRow): string =>
  row.go_price === null ? "–" : row.go_price.toFixed(2);
/** Commission-earning volume: spot, spot with min price and forward. */
const soldGwh = (row: ArrRow): number =>
  row.gwh_spot +
  row.gwh_spot_min_price +
  row.gwh_forward_committed +
  row.gwh_forward_to_be_sold;

function applyPrice() {
  const parsed = Number(priceInput.value);
  if (priceInput.value === "" || Number.isNaN(parsed) || parsed < 0) return;
  goPrice.value = parsed;
}

function resetPrice() {
  priceInput.value = "";
  goPrice.value = null;
}

const moneyTooltip = (label: string | undefined, value: number) =>
  `${label}: ${formatMoney(value)} €`;

function arrChartConfig(): ChartConfiguration {
  const labels = rows.value.map((row) => periodLabel(row.year, row.month));
  return {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          type: "line",
          label: "Net ARR",
          data: rows.value.map((row) => row.net_arr_total),
          borderColor: "#0dcaf0",
          backgroundColor: "#0dcaf0",
          tension: 0.25,
          pointRadius: 3,
          order: 0,
        },
        {
          type: "line",
          label: "Realized gross revenue, trailing 12 months",
          data: rows.value.map((row) => row.realized_gross_ttm ?? 0),
          borderColor: "#6c757d",
          backgroundColor: "#6c757d",
          borderDash: [6, 4],
          tension: 0.25,
          pointRadius: 3,
          order: 0,
        },
        {
          label: "ARR spot",
          data: rows.value.map((row) => row.arr_spot),
          backgroundColor: "rgba(13, 110, 253, 0.7)",
          stack: "arr",
          order: 1,
        },
        {
          label: "ARR forward",
          data: rows.value.map((row) => row.arr_forward),
          backgroundColor: "rgba(25, 135, 84, 0.7)",
          stack: "arr",
          order: 1,
        },
        {
          label: "Platform fees ARR",
          data: rows.value.map((row) => row.platform_fees_arr),
          backgroundColor: "rgba(255, 193, 7, 0.7)",
          stack: "arr",
          order: 1,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, title: { display: true, text: "EUR / year" } },
      },
      plugins: {
        legend: { position: "top", labels: { boxWidth: 12 } },
        tooltip: {
          filter: (item) => item.parsed.y !== null,
          callbacks: {
            label: (ctx) => moneyTooltip(ctx.dataset.label, ctx.parsed.y ?? 0),
          },
        },
      },
    },
  };
}

function gwhChartConfig(): ChartConfiguration {
  const labels = rows.value.map((row) => periodLabel(row.year, row.month));
  const datasets: ChartConfiguration["data"]["datasets"] = ARR_STRATEGIES.map(
    (strategy) => ({
      label: strategy.sold
        ? strategy.label
        : `${strategy.label} (no commission)`,
      data: rows.value.map((row) => row[strategy.key]),
      backgroundColor: strategy.color,
      stack: "gwh",
      yAxisID: "y",
    })
  );
  datasets.push({
    type: "line",
    label: "Spot GO price (€/MWh)",
    data: rows.value.map((row) => row.go_price),
    borderColor: "#dc3545",
    backgroundColor: "#dc3545",
    tension: 0.25,
    pointRadius: 3,
    yAxisID: "price",
  });
  return {
    type: "bar",
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, title: { display: true, text: "GWh / year" } },
        price: {
          position: "right",
          grid: { drawOnChartArea: false },
          title: { display: true, text: "€/MWh" },
          beginAtZero: true,
        },
      },
      plugins: {
        legend: { position: "top", labels: { boxWidth: 12 } },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              ctx.dataset.yAxisID === "price"
                ? `${ctx.dataset.label}: ${(ctx.parsed.y ?? 0).toFixed(2)}`
                : `${ctx.dataset.label}: ${formatWholeNumber(ctx.parsed.y ?? 0)} GWh`,
          },
        },
      },
    },
  };
}

async function renderCharts() {
  await nextTick();
  if (!rows.value.length) return;
  arrChart.render(arrChartConfig());
  gwhChart.render(gwhChartConfig());
}

watch(() => [props.data, props.isLoading], renderCharts, {
  immediate: true,
  flush: "post",
});
</script>

<style scoped lang="scss">
.chart-lg {
  height: 340px;
}

.price-input {
  width: auto;

  input {
    width: 110px;
  }
}

:deep(.cursor-pointer) {
  cursor: pointer;
}
</style>
